import { useContext, useEffect, useState } from "react";
import AuthContext from "contexts/AuthContext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { HistoricoFiscal, ItemNotaFiscalSaidaCalculoTributo, NotaFiscalSaidaPendenciaIntegracao, NotaFiscalSaidaTentativaSefaz } from "model/vendas/NotaFiscalSaidaHistoricoFiscal";
import NotaFiscalSaidaHistoricoFiscalService from "service/vendas/NotaFiscalSaidaHistoricoFiscalService";
import { converterDataHora } from "utils/convert";

const service = new NotaFiscalSaidaHistoricoFiscalService();

const formatarMoeda = (valor: number | null | undefined) => valor == null
    ? "—"
    : valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatarPercentual = (valor: number | null | undefined) => valor == null
    ? "—"
    : `${valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} %`;

export default function NotaFiscalSaidaHistoricoFiscal({ id }: { id: number }) {
    const { acoes } = useContext(AuthContext);
    const gerenciar = acoes.some(a => a.sigla === "NOTA_FISCAL_SAIDA_REPROCESSAR");
    const [historico, setHistorico] = useState<HistoricoFiscal | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState("");
    const [detalhe, setDetalhe] = useState<ItemNotaFiscalSaidaCalculoTributo | null>(null);
    const [revisao, setRevisao] = useState(0);
    useEffect(() => {
        let ativo = true;
        setCarregando(true); setErro("");
        service.listar(id).then(dados => { if (ativo) setHistorico(dados); })
            .catch(() => { if (ativo) setErro("Não foi possível carregar o histórico fiscal."); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [id, revisao]);
    async function repetir(p: NotaFiscalSaidaPendenciaIntegracao) {
        try { await service.repetirIntegracao(id, p.id); setRevisao(r => r + 1); }
        catch { setErro("Não foi possível agendar a integração novamente."); }
    }
    return <div>
        <Button label="Atualizar histórico" icon="pi pi-refresh" outlined loading={carregando} onClick={() => setRevisao(r => r + 1)} />
        {erro && <p role="alert" className="p-error">{erro}</p>}
        {historico && <TabView>
            <TabPanel header="Memória de cálculo">
                <DataTable value={historico.calculos} paginator rows={12} emptyMessage="Nenhum cálculo registrado">
                    <Column field="idItemNotaFiscalSaida" header="Item" /><Column field="versaoCalculo" header="Versão" /><Column field="tributo" header="Tributo" />
                    <Column field="baseOriginal" header="Base original"
                        body={(calculo: ItemNotaFiscalSaidaCalculoTributo) => formatarMoeda(calculo.baseOriginal)} />
                    <Column field="baseCalculo" header="Base aplicada"
                        body={(calculo: ItemNotaFiscalSaidaCalculoTributo) => formatarMoeda(calculo.baseCalculo)} />
                    <Column field="aliquotaNominal" header="Alíquota nominal"
                        body={(calculo: ItemNotaFiscalSaidaCalculoTributo) => formatarPercentual(calculo.aliquotaNominal)} />
                    <Column field="aliquotaEfetiva" header="Alíquota efetiva"
                        body={(calculo: ItemNotaFiscalSaidaCalculoTributo) => formatarPercentual(calculo.aliquotaEfetiva)} />
                    <Column field="valorTributo" header="Valor"
                        body={(calculo: ItemNotaFiscalSaidaCalculoTributo) => formatarMoeda(calculo.valorTributo)} />
                    <Column header="Detalhes" body={(m: ItemNotaFiscalSaidaCalculoTributo) => <Button label="Parâmetros e cálculo" text onClick={() => setDetalhe(m)} />} />
                </DataTable>
            </TabPanel>
            <TabPanel header="Validações">
                <DataTable value={historico.validacoes} paginator rows={10} emptyMessage="Nenhum problema registrado">
                    <Column field="dataValidacao" header="Data" /><Column field="versaoCalculo" header="Versão" /><Column field="etapa" header="Etapa" />
                    <Column field="severidade" header="Severidade" /><Column field="campo" header="Campo" /><Column field="mensagem" header="Mensagem" />
                </DataTable>
            </TabPanel>
            <TabPanel header="Operações SEFAZ">
                <DataTable value={historico.operacoes} paginator rows={10} emptyMessage="Nenhuma operação registrada">
                    <Column field="id" header="Operação" /><Column field="tipoOperacao" header="Tipo" /><Column field="ambiente" header="Ambiente" />
                    <Column field="status" header="Situação" /><Column field="recibo" header="Recibo" /><Column field="tentativas" header="Tentativas" />
                    <Column field="cstat" header="cStat" /><Column field="motivo" header="Motivo" />
                </DataTable>
                <h4 className="mt-3">Tentativas de comunicação</h4>
                <DataTable value={historico.tentativas} paginator rows={10} emptyMessage="Nenhuma tentativa registrada">
                    <Column field="idNotaFiscalSaidaOperacaoSefaz" header="Operação" /><Column field="tentativa" header="Tentativa" />
                    <Column field="inicio" header="Início"
                        body={(tentativa: NotaFiscalSaidaTentativaSefaz) => tentativa.inicio
                            ? converterDataHora(new Date(tentativa.inicio))
                            : "—"} />
                    <Column field="resultado" header="Resultado" /><Column field="statusHttp" header="HTTP" /><Column field="cstat" header="cStat" /><Column field="erro" header="Erro" />
                </DataTable>
            </TabPanel>
            <TabPanel header="Integrações internas">
                <DataTable value={historico.pendencias} emptyMessage="Nenhuma integração pendente">
                    <Column field="tipoIntegracao" header="Integração" /><Column field="status" header="Situação" /><Column field="tentativas" header="Tentativas" />
                    <Column field="ultimoErro" header="Último erro" /><Column field="proximaTentativa" header="Próxima tentativa" />
                    {gerenciar && <Column body={(p: NotaFiscalSaidaPendenciaIntegracao) => <Button label="Tentar novamente" outlined disabled={["CONCLUIDA","DISPENSADA"].includes(p.status)} onClick={() => { void repetir(p); }} />} />}
                </DataTable>
            </TabPanel>
        </TabView>}
        <Dialog header="Parâmetros e memória do cálculo" visible={detalhe !== null} onHide={() => setDetalhe(null)} style={{ width: "min(900px, 95vw)" }}>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify({ parametros: detalhe?.parametrosAplicados, etapas: detalhe?.etapasCalculo }, null, 2)}</pre>
        </Dialog>
    </div>;
}
